import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'app/store';
import Toggle, { ToggleSize } from 'common/components/inputs/toggle';
import Tooltip from 'common/components/tooltip/tooltip-v2';
import {
  useDeleteClientIntegrationMutation,
  useManageClientIntegrationStateMutation
} from 'app/apis/commandApi';
import Spinner from 'common/components/loading/spinner';
import { useNavigate } from 'react-router-dom';
import { ClientIntegrationDTO } from '../clientIntegration.model';
import { useGetClientIntegrationsQuery } from 'app/apis/queryApi';
import { onError, onSuccess } from 'features/toast/toast.slice';
import { addListener } from '@reduxjs/toolkit';
import { closeModal, confirmModal, openModal } from 'features/modal/modalSlice';
import { useAuth } from 'features/auth/hooks/useAuth';
import { ButtonTheme } from 'common/components/buttons/button-theme';

export interface ClientIntegrationTableActionsProps {
  clientIntegration: ClientIntegrationDTO;
}

const ClientIntegrationTableActions = ({
  clientIntegration
}: ClientIntegrationTableActionsProps) => {
  const dispatch = useDispatch();
  const { siteId } = useSelector((state: RootState) => state.selectedSite);
  const { isSuperUser } = useAuth();
  const [manageClientIntegrationState, { isLoading: mutationLoading }] =
    useManageClientIntegrationStateMutation();

  const navigate = useNavigate();

  const isDirectAPI = clientIntegration.dataSource === 'DirectAPI';
  const isDisabled = clientIntegration.state === 'Disabled';
  const isReauth = clientIntegration.state === 'Re-Authentication';

  const { isFetching } = useGetClientIntegrationsQuery({ siteId });

  const handleStateChange = async (action: 'Enabled' | 'Disabled') => {
    if (!siteId) return;
    try {
      await manageClientIntegrationState({
        siteId,
        platformCategory: clientIntegration.platformCategory,
        platform: clientIntegration.platform,
        clientIntegrationType: clientIntegration.type,
        newState: action
      }).unwrap();

      if (action === 'Enabled') {
        handleConfigureClick();
        dispatch(
          onSuccess({
            title: 'Integration Enabled',
            description: `This requires Re-Authentication.`
          })
        );
      } else {
        dispatch(
          onSuccess({
            title: 'Integration Disabled',
            description: `Integration has been successfully disabled.`
          })
        );
      }
    } catch (err) {
      console.error('Failed to change state:', err);
    }
  };

  const formatPlatform = (platform: string) =>
    platform.replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase();

  const formattedPlatform = formatPlatform(clientIntegration.platform);

  const handleConfigureClick = () => {
    navigate(`/settings/integrations/setup/${formattedPlatform}`);
  };

  const handleRunClientIntegrationClick = () => {
    navigate(
      `/settings/integrations/${clientIntegration.platformCategory.toLocaleLowerCase()}/${formattedPlatform}/${clientIntegration.type.toLocaleLowerCase()}/run`
    );
  };

  const [deleteClientIntegration] = useDeleteClientIntegrationMutation();

  const handleDeleteClick = () => {
    dispatch(
      addListener({
        actionCreator: confirmModal,
        effect: async (action, listenerApi) => {
          listenerApi.cancelActiveListeners();

          try {
            await deleteClientIntegration({
              siteId,
              platformCategory: clientIntegration.platformCategory,
              platform: clientIntegration.platform,
              clientIntegrationType: clientIntegration.type
            }).unwrap();

            dispatch(
              onSuccess({
                title: 'Integration Deleted',
                description: 'Client integration was successfully deleted.'
              })
            );
          } catch (error) {
            console.error('Failed to delete integration:', error);
            dispatch(
              onError({
                title: 'Deletion Failed',
                description: 'Could not delete client integration.'
              })
            );
          }

          listenerApi.dispatch(closeModal());
          listenerApi.unsubscribe();
        }
      })
    );

    dispatch(
      openModal({
        modalProps: {
          title: 'Are you sure you want to delete this integration?',
          content: 'This action cannot be undone.',
          confirmButtonText: 'Delete',
          cancelButtonText: 'Cancel',
          confirmButtonTheme: ButtonTheme.PRIMARY,
          cancelButtonTheme: ButtonTheme.CANCEL
        },
        modalType: 'deleteClientIntegration'
      })
    );
  };

  return (
    <div className="text-sm w-full flex items-center justify-center">
      {isDirectAPI && clientIntegration.state !== 'Re-Authentication' && (
        <Tooltip
          tooltipContent={
            <div className="whitespace-nowrap">
              {isDisabled ? 'Enable' : 'Disable'}
            </div>
          }
        >
          <div className="flex items-center">
            {mutationLoading || isFetching ? (
              <div className="flex justify-center mr-2">
                <Spinner />
              </div>
            ) : (
              <Toggle
                checked={!isDisabled}
                label=""
                identifier={`toggle-${clientIntegration.platformCategory}#${clientIntegration.platform}#${clientIntegration.type}`}
                size={ToggleSize.Small}
                onChange={() => {
                  void handleStateChange(isDisabled ? 'Enabled' : 'Disabled');
                }}
              />
            )}
          </div>
        </Tooltip>
      )}

      {isDirectAPI && isSuperUser && !isDisabled && (
        <Tooltip tooltipContent={<div className="whitespace-nowrap">Run</div>}>
          <button
            onClick={handleRunClientIntegrationClick}
            className="mt-1 mr-2"
          >
            <i className="ra-icons-generic-play text-sm" />
          </button>
        </Tooltip>
      )}

      {isDirectAPI && (
        <Tooltip
          tooltipContent={
            <div className="whitespace-nowrap">
              {isReauth ? 'Re-Authenticate' : 'Configure'}
            </div>
          }
        >
          <button onClick={handleConfigureClick} className="mt-1">
            {isReauth ? (
              <i className="ra-icons-generic-warning-2 text-sm" />
            ) : (
              <i className="ra-icons-generic-edit text-sm" />
            )}
          </button>
        </Tooltip>
      )}

      {isDirectAPI && (
        <Tooltip
          tooltipContent={<div className="whitespace-nowrap">Delete</div>}
        >
          <button onClick={handleDeleteClick} className="mt-1 ml-2">
            <i className="ra-icons-generic-bin text-sm" />
          </button>
        </Tooltip>
      )}
    </div>
  );
};

export default ClientIntegrationTableActions;
